import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { ActionCard, DataList, KeyValue, StatusPill, type DataListColumn } from "@ozpanel/kit";
import { Panel, Section, StateView, formatBytes, useAuth } from "@ozpanel/ui";

/**
 * Garage (the replicated object store) and JuiceFS (the filesystem whose content lives in it).
 *
 * Both modules shipped with a status route and an enable route and nothing in the console called
 * either (CLAUDE.md rule 8). Enabling them was a curl, and "is the object store actually holding
 * quorum" could only be asked on the node.
 *
 * Every value here is read from /garage/status and /juicefs/status. Where a field is absent the
 * screen says "not reported" — an absent measurement rendered as blank reads as "fine", and the
 * absence is often the fault.
 */

// ── Shapes — exactly what the Go handlers send ─────────────────────────────────────────────

/** A node's self-published Garage address. The Go struct has NO json tags, so the wire keys are
 *  the Go field names: Node, ID, Addr. Renaming them here would render every row empty. */
interface GaragePublished {
  Node: string;
  ID: string;
  Addr: string;
}
interface GarageHealth {
  status: string;
  knownNodes: number;
  connectedNodes: number;
  storageNodes: number;
  storageNodesUp: number;
  partitions: number;
  partitionsQuorum: number;
  partitionsAllOk: number;
}
interface GarageNode {
  id: string;
  addr: string;
  isUp: boolean;
  role: { zone: string; capacity: number | null } | null;
  dataPartition: { available: number; total: number } | null;
}
interface GarageStatus {
  enabled: boolean;
  binary_placed: boolean;
  running_here?: boolean;
  published_nodes?: GaragePublished[] | null;
  published_error?: string;
  health?: GarageHealth;
  health_error?: string;
  cluster?: { layoutVersion: number; nodes: GarageNode[] | null };
  cluster_error?: string;
}

interface JuiceVolume {
  volume: string;
  database: string;
  bucket: string;
  mountpoint: string;
  uuid: string;
  kernel_fstype: string;
  unit_active?: boolean;
  /** The metadata engine this volume is on (postgres | redis), or why it could not be read. */
  engine?: string;
  engine_error?: string;
  /** The paths this volume serves (backend rows), each with THIS node's kernel answer at the path. */
  serves?: { mountpoint: string; state: string; kernel_fstype: string }[] | null;
}
interface JuiceStatus {
  enabled: boolean;
  binary_ok: boolean;
  binary_problem?: string;
  volumes: JuiceVolume[] | null;
  serves_error?: string;
  glusterfs_retired_here?: boolean;
}

/** What POST /storage/juicefs/engine answers: the storageswitch.Job it started (job.go json tags). */
interface EngineJob {
  id: number;
  mountpoint: string;
  from: string;
  to: string;
  coordinator: string;
  state: string;
  step: string;
}

/** What /garage/enable and /juicefs/enable answer on success. */
interface EnableResult {
  enabled?: boolean;
  this_node?: unknown;
  this_node_error?: string;
  note?: string;
}

// ── Small shared pieces ────────────────────────────────────────────────────────────────────

const notReported = <span className="text-muted">not reported</span>;

/** An error string exactly as the server sent it. */
function ErrorBox({ label, text }: { label: string; text: string }) {
  return (
    <div className="rounded border border-[color-mix(in_oklab,var(--ozp-crit)_38%,transparent)] bg-critsoft px-3 py-2 text-[12px] leading-relaxed text-crit">
      <b>{label}:</b> <span className="font-mono break-all">{text}</span>
    </div>
  );
}

function EnableResultView({ value }: { value: unknown }) {
  const r = (value ?? {}) as EnableResult;
  return (
    <div className="space-y-2">
      <div>
        Recorded: <StatusPill tone={r.enabled ? "ok" : "neutral"}>{r.enabled ? "enabled" : "disabled"}</StatusPill>
      </div>
      {r.this_node_error && <ErrorBox label="This node's converge failed" text={r.this_node_error} />}
      {r.note && <div className="text-muted">{r.note}</div>}
      {r.this_node !== undefined && (
        <div>
          <div className="mb-1 text-[11px] uppercase tracking-wide text-muted">This node's converge reported</div>
          <pre className="max-h-56 overflow-auto whitespace-pre-wrap rounded border border-line bg-bg px-2 py-1.5 font-mono text-[11px]">
            {JSON.stringify(r.this_node, null, 1)}
          </pre>
        </div>
      )}
    </div>
  );
}

function boolOrNotReported(v: boolean | undefined, good = "yes", bad = "no") {
  if (v === undefined) return notReported;
  return <StatusPill tone={v ? "ok" : "bad"}>{v ? good : bad}</StatusPill>;
}

// ── Garage ────────────────────────────────────────────────────────────────────────────────

function healthTone(s: string | undefined): "ok" | "warn" | "bad" | "neutral" {
  if (s === "healthy") return "ok";
  if (s === "degraded") return "warn";
  if (s === "unavailable") return "bad";
  return "neutral";
}

export function GarageOps() {
  const { client } = useAuth();
  const q = useQuery({
    queryKey: ["garage-status"],
    queryFn: () => client.apiFetch<GarageStatus>("/garage/status"),
    refetchInterval: 15000,
    retry: false,
  });
  const d = q.data;
  const published = d?.published_nodes ?? [];
  const byId = new Map(published.map((p) => [p.ID, p]));
  const nodes = d?.cluster?.nodes ?? [];
  const h = d?.health;

  const nodeCols: DataListColumn<GarageNode>[] = [
    {
      key: "id",
      label: "Node",
      render: (n) => {
        const p = byId.get(n.id);
        return (
          <span>
            {p ? p.Node : <span className="text-muted">not published by any node</span>}
            <span className="ml-2 font-mono text-[11px] text-faint">{n.id.slice(0, 16)}</span>
          </span>
        );
      },
    },
    { key: "addr", label: "Address", render: (n) => <span className="font-mono text-[12px]">{n.addr || "—"}</span> },
    { key: "isUp", label: "Up", render: (n) => <StatusPill tone={n.isUp ? "ok" : "bad"}>{n.isUp ? "up" : "down"}</StatusPill> },
    {
      key: "role",
      label: "Zone",
      // No role means the node is not in the applied layout: it holds no data at all.
      render: (n) => (n.role ? <span className="font-mono text-[12px]">{n.role.zone}</span> : <StatusPill tone="warn">not in layout</StatusPill>),
    },
    {
      key: "capacity",
      label: "Capacity",
      render: (n) => (n.role ? (n.role.capacity == null ? <span className="text-muted">gateway (none)</span> : formatBytes(n.role.capacity)) : "—"),
    },
    {
      key: "disk",
      label: "Disk free / total",
      render: (n) =>
        n.dataPartition ? (
          <span className="font-mono text-[12px]">
            {formatBytes(n.dataPartition.available)} / {formatBytes(n.dataPartition.total)}
          </span>
        ) : (
          notReported
        ),
    },
  ];

  const pubCols: DataListColumn<GaragePublished>[] = [
    { key: "Node", label: "Node", sortable: true },
    { key: "ID", label: "Garage id", render: (p) => <span className="font-mono text-[11.5px] break-all">{p.ID}</span> },
    { key: "Addr", label: "Published address", render: (p) => <span className="font-mono text-[11.5px] break-all">{p.Addr}</span> },
  ];

  return (
    <div className="space-y-4">
      <Section
        title="Garage object store"
        actions={d ? <StatusPill tone={d.enabled ? "ok" : "neutral"}>{d.enabled ? "enabled" : "disabled"}</StatusPill> : undefined}
        bodyClassName="p-0"
      >
        <StateView loading={q.isLoading} error={q.error} errorContext="Garage's status">
          {d && (
            <KeyValue
              rows={[
                { key: "enabled", label: "Enabled on this cluster", value: d.enabled, render: () => <StatusPill tone={d.enabled ? "ok" : "neutral"}>{d.enabled ? "yes" : "no"}</StatusPill> },
                { key: "binary_placed", label: "Binary placed on this node", value: d.binary_placed },
                {
                  key: "running_here",
                  label: "Running on this node",
                  value: d.running_here,
                  render: () => boolOrNotReported(d.running_here, "running", "not running"),
                  hint: d.enabled && d.running_here === false ? "Enabled but not running here — this node holds none of the data until it is." : undefined,
                },
              ]}
            />
          )}
        </StateView>
      </Section>

      <Section
        title="Health"
        hint="Garage's own verdict on whether data is reachable"
        actions={h ? <StatusPill tone={healthTone(h.status)}>{h.status}</StatusPill> : undefined}
        bodyClassName="p-0"
      >
        <div className="space-y-3 p-4">
          {d?.health_error && <ErrorBox label="Health could not be read" text={d.health_error} />}
          {h ? (
            <Panel className="overflow-hidden">
              <KeyValue
                dense
                rows={[
                  {
                    key: "quorum",
                    label: "Partitions at quorum",
                    value: null,
                    render: () => (
                      <StatusPill tone={h.partitionsQuorum === h.partitions ? "ok" : "bad"}>
                        {h.partitionsQuorum} / {h.partitions}
                      </StatusPill>
                    ),
                    hint: h.partitionsQuorum < h.partitions ? "Some partitions cannot reach a write quorum — writes to them fail." : undefined,
                  },
                  {
                    key: "allok",
                    label: "Partitions with every copy",
                    value: null,
                    render: () => (
                      <StatusPill tone={h.partitionsAllOk === h.partitions ? "ok" : "warn"}>
                        {h.partitionsAllOk} / {h.partitions}
                      </StatusPill>
                    ),
                  },
                  {
                    key: "storage",
                    label: "Storage nodes up",
                    value: null,
                    render: () => (
                      <StatusPill tone={h.storageNodesUp === h.storageNodes ? "ok" : "warn"}>
                        {h.storageNodesUp} / {h.storageNodes}
                      </StatusPill>
                    ),
                  },
                  { key: "connected", label: "Nodes connected / known", value: `${h.connectedNodes} / ${h.knownNodes}` },
                ]}
              />
            </Panel>
          ) : (
            !q.isLoading &&
            !d?.health_error && (
              <div className="text-[12.5px] text-muted">
                Not reported. This node asks Garage for its health only while Garage is running here
                {d?.running_here === false ? " — and it is not." : "."}
              </div>
            )
          )}
        </div>
      </Section>

      <Section
        title="Nodes as Garage sees them"
        hint={d?.cluster ? `layout version ${d.cluster.layoutVersion} · ${nodes.length} node(s)` : undefined}
        bodyClassName="p-0"
      >
        <div className="space-y-3 p-4">
          {d?.cluster_error && <ErrorBox label="Cluster status could not be read" text={d.cluster_error} />}
          {d?.cluster ? (
            <DataList
              items={nodes}
              columns={nodeCols}
              getRowKey={(n) => n.id}
              emptyMessage="Garage reports no nodes."
              emptyHint="Garage answered with an empty node list — not a statement that storage is healthy."
            />
          ) : (
            !q.isLoading &&
            !d?.cluster_error && (
              <div className="text-[12.5px] text-muted">
                Not reported. The node list is asked of Garage only while it runs on this node.
              </div>
            )
          )}
        </div>
      </Section>

      <Section title="Published addresses" hint="what each ozpanel node published for its peers to connect to" bodyClassName="p-0">
        <div className="space-y-3 p-4">
          {d?.published_error ? (
            <ErrorBox label="Published addresses could not be read" text={d.published_error} />
          ) : (
            <StateView loading={q.isLoading} error={q.error} errorContext="the published addresses">
              <DataList
                items={published}
                columns={pubCols}
                getRowKey={(p) => p.Node}
                emptyMessage="No node has published a Garage address."
                emptyHint="Expected until Garage is enabled and each node's converge has run."
              />
            </StateView>
          )}
        </div>
      </Section>

      <Section title="Control">
        <ActionCard
          title={d?.enabled ? "Disable Garage" : "Enable Garage"}
          description="Runs Garage on every node of the cluster. This node converges now; every other node on its next gate pass (about two minutes)."
          actionLabel={d?.enabled ? "Disable" : "Enable"}
          danger={d?.enabled ? "high" : "medium"}
          unavailable={!d ? "Garage's current state has not been read yet." : undefined}
          confirm={{
            title: d?.enabled ? "Disable Garage on every node?" : "Enable Garage on every node?",
            body: d?.enabled ? (
              <p className="text-[12.5px] leading-relaxed">
                Garage runs on every node; disabling it stops the daemons cluster-wide and keeps the
                data on disk. JuiceFS keeps its file content in Garage, so any JuiceFS mount stops
                being able to read or write file content while it is off.
              </p>
            ) : (
              <p className="text-[12.5px] leading-relaxed">
                This runs Garage on every node. Disabling it later stops the daemons and keeps the data.
              </p>
            ),
            confirmLabel: d?.enabled ? "Disable Garage" : "Enable Garage",
          }}
          run={() => client.apiFetch<EnableResult>("/garage/enable", { method: "POST", body: JSON.stringify({ enabled: !d?.enabled }) })}
          invalidates={[["garage-status"], ["juicefs-status"]]}
          renderResult={(r) => <EnableResultView value={r} />}
        />
      </Section>
    </div>
  );
}

// ── JuiceFS ───────────────────────────────────────────────────────────────────────────────

export function JuiceFSOps() {
  const { client } = useAuth();
  const q = useQuery({
    queryKey: ["juicefs-status"],
    queryFn: () => client.apiFetch<JuiceStatus>("/juicefs/status"),
    refetchInterval: 15000,
    retry: false,
  });
  const d = q.data;
  const vols = d?.volumes ?? [];
  // Measured, never a constant: which paths each volume serves (rows) and what this node's kernel has
  // there. This page said "staging — nothing consumes them" as fixed text after everything had moved.
  const served = vols.flatMap((v) => (v.serves ?? []).map((s) => ({ ...s, volume: v.volume })));

  const cols: DataListColumn<JuiceVolume>[] = [
    {
      key: "volume",
      label: "Volume",
      sortable: true,
      render: (v) => (
        <span>
          {v.volume}
          <span className="ml-2 font-mono text-[11px] text-faint">
            db {v.database} · bucket {v.bucket}
          </span>
        </span>
      ),
    },
    {
      key: "mountpoint",
      label: "Mountpoint",
      render: (v) => (
        <span className="flex flex-col gap-1">
          <span className="font-mono text-[12px]">{v.mountpoint}</span>
          {(v.serves ?? []).length === 0 ? (
            <StatusPill tone="neutral">serves nothing</StatusPill>
          ) : (
            (v.serves ?? []).map((s) => (
              <span key={s.mountpoint} className="flex items-center gap-2">
                <StatusPill tone={s.kernel_fstype === "fuse.juicefs" ? "ok" : "bad"}>
                  {s.kernel_fstype === "fuse.juicefs" ? "serving" : "NOT mounted here"}
                </StatusPill>
                <span className="font-mono text-[11.5px]">{s.mountpoint}</span>
              </span>
            ))
          )}
        </span>
      ),
    },
    {
      key: "uuid",
      label: "Formatted",
      render: (v) => (v.uuid ? <span className="font-mono text-[11.5px] break-all">{v.uuid}</span> : <StatusPill tone="warn">not formatted</StatusPill>),
    },
    {
      key: "kernel_fstype",
      label: "Kernel mount",
      // The KERNEL's answer from /proc/mounts, not the unit's. A path that is not a juicefs mount
      // accepts writes onto local disk — the same trap as an unmounted /mnt/cluster.
      render: (v) =>
        v.kernel_fstype === "fuse.juicefs" ? (
          <span className="flex items-center gap-2">
            <StatusPill tone="ok">mounted</StatusPill>
            <span className="font-mono text-[11px] text-faint">{v.kernel_fstype}</span>
          </span>
        ) : (
          <span className="flex items-center gap-2">
            <StatusPill tone={d?.enabled ? "bad" : "neutral"}>NOT mounted</StatusPill>
            <span className="font-mono text-[11px] text-faint">{v.kernel_fstype || "no mount entry"}</span>
          </span>
        ),
    },
    {
      key: "unit_active",
      label: "Unit",
      render: (v) =>
        v.unit_active === undefined ? notReported : <StatusPill tone={v.unit_active ? "ok" : d?.enabled ? "bad" : "neutral"}>{v.unit_active ? "active" : "inactive"}</StatusPill>,
    },
  ];

  return (
    <div className="space-y-4">
      <Section
        title="JuiceFS"
        actions={d ? <StatusPill tone={d.enabled ? "ok" : "neutral"}>{d.enabled ? "enabled" : "disabled"}</StatusPill> : undefined}
        bodyClassName="p-0"
      >
        <StateView loading={q.isLoading} error={q.error} errorContext="JuiceFS's status">
          {d && (
            <>
              <KeyValue
                rows={[
                  { key: "enabled", label: "Enabled on this cluster", value: d.enabled, render: () => <StatusPill tone={d.enabled ? "ok" : "neutral"}>{d.enabled ? "yes" : "no"}</StatusPill> },
                  { key: "binary_ok", label: "Binary usable on this node", value: d.binary_ok },
                ]}
              />
              {d.binary_problem && (
                <div className="px-4 pb-3">
                  <ErrorBox label="Binary problem" text={d.binary_problem} />
                </div>
              )}
            </>
          )}
        </StateView>
      </Section>

      <Section title="Volumes" hint={d ? `${vols.length} volume(s) on this node` : undefined} bodyClassName="p-0">
        <div className="border-b border-line px-4 py-2.5 text-[12px] leading-relaxed text-muted">
          {d?.serves_error ? (
            <ErrorBox label="Could not read what JuiceFS serves" text={d.serves_error} />
          ) : served.length === 0 ? (
            <>These volumes serve <b>no path yet</b> — every shared mountpoint is still on its previous backend.</>
          ) : (
            <>
              JuiceFS serves <b>{served.length}</b> path(s):{" "}
              {served.map((s, i) => (
                <span key={s.mountpoint}>
                  {i > 0 && ", "}
                  <span className="font-mono">{s.mountpoint}</span>
                </span>
              ))}
              .{" "}
              {d?.glusterfs_retired_here
                ? "GlusterFS is retired on this node."
                : "GlusterFS is not retired on this node."}{" "}
              The status per path is this node's kernel mount, read now.
            </>
          )}
        </div>
        <div className="p-4">
          <StateView loading={q.isLoading} error={q.error} errorContext="the JuiceFS volumes">
            <DataList
              items={vols}
              columns={cols}
              getRowKey={(v) => v.volume}
              emptyMessage="This node reports no JuiceFS volumes."
              emptyHint="The module declares its volumes in code, so an empty list is itself unexpected."
            />
          </StateView>
        </div>
      </Section>

      <Section title="Metadata engine" hint="Where each volume's index (names, folders, which blocks form each file) lives. The file contents stay in Garage either way.">
        <div className="space-y-3">
          {vols.map((v) => {
            const engine = v.engine ?? "unknown";
            const to = engine === "redis" ? "postgres" : "redis";
            return (
              <ActionCard
                key={v.volume}
                title={`${v.volume}: ${engine === "unknown" ? "engine unknown" : engine === "redis" ? "Redis (via Sentinel)" : "PostgreSQL (via PgBouncer)"}`}
                description={`Move ${v.volume}'s metadata to ${to === "redis" ? "Redis" : "PostgreSQL"}. This is a migration, not a flag: writers pause on every node, the metadata is copied and verified file by file, every node remounts, and writers resume. Any failure before resume goes back; the old copy is kept.`}
                actionLabel={`Move to ${to === "redis" ? "Redis" : "PostgreSQL"}`}
                danger="high"
                unavailable={engine === "unknown" ? `The engine could not be read${v.engine_error ? ": " + v.engine_error : ""}.` : undefined}
                confirm={{
                  title: `Move ${v.volume}'s metadata to ${to === "redis" ? "Redis" : "PostgreSQL"}?`,
                  body: (
                    <p className="text-[12.5px] leading-relaxed">
                      Mail, web, apps and cron pause on every node for the copy (typically under a minute), then resume. Progress is under{" "}
                      <Link to="/storage?tab=switch" className="text-accent hover:underline">
                        Storage switch jobs
                      </Link>
                      .
                    </p>
                  ),
                  confirmLabel: `Move to ${to === "redis" ? "Redis" : "PostgreSQL"}`,
                }}
                run={() => client.apiFetch<EngineJob>("/storage/juicefs/engine", { method: "POST", body: JSON.stringify({ volume: v.volume, to }) })}
                invalidates={[["storage-switch-jobs"]]}
                renderResult={(r) => {
                  const j = r as EngineJob;
                  return (
                    <span>
                      Migration job <span className="font-mono">#{j.id}</span> started on {j.coordinator}: {j.from} → {j.to}.{" "}
                      <Link to={`/storage?tab=switch&job=${j.id}`} className="text-accent hover:underline">
                        Follow its progress
                      </Link>
                      .
                    </span>
                  );
                }}
              />
            );
          })}
        </div>
      </Section>

      <FsckPanel volumes={vols.map((v) => v.volume)} />

      <Section title="Control">
        <ActionCard
          title={d?.enabled ? "Disable JuiceFS" : "Enable JuiceFS"}
          description="Formats and mounts the JuiceFS volumes on every node. It needs Garage enabled and healthy first; the server refuses otherwise and says why."
          actionLabel={d?.enabled ? "Disable" : "Enable"}
          danger={d?.enabled ? "high" : "medium"}
          unavailable={
            !d
              ? "JuiceFS's current state has not been read yet."
              : d.enabled && served.length > 0
                ? `JuiceFS serves ${served.map((s) => s.mountpoint).join(", ")} — disabling it would unmount them on every node. Move them with the switch job first (the server refuses too).`
                : undefined
          }
          confirm={{
            title: d?.enabled ? "Disable JuiceFS on every node?" : "Enable JuiceFS on every node?",
            body: (
              <p className="text-[12.5px] leading-relaxed">
                {d?.enabled
                  ? "Every node unmounts its JuiceFS volumes on its next gate pass. The metadata (PostgreSQL) and content (Garage) are kept."
                  : "Every node mounts the JuiceFS volumes on its next gate pass; this node converges now."}
              </p>
            ),
            confirmLabel: d?.enabled ? "Disable JuiceFS" : "Enable JuiceFS",
          }}
          run={() => client.apiFetch<EnableResult>("/juicefs/enable", { method: "POST", body: JSON.stringify({ enabled: !d?.enabled }) })}
          invalidates={[["juicefs-status"]]}
          renderResult={(r) => <EnableResultView value={r} />}
        />
      </Section>
    </div>
  );
}

interface FsckCheck {
  volume: string;
  path: string;
  clean: boolean;
  findings: string[];
  repairable: string[];
}

function FsckView({ value }: { value: unknown }) {
  const r = value as { check?: FsckCheck; before?: FsckCheck; after?: FsckCheck; repaired?: string[]; failed?: string[]; note?: string };
  const one = (c: FsckCheck | undefined, label: string) =>
    c ? (
      <div className="mt-2">
        <div className="text-[12px] font-medium">
          {label}: <StatusPill tone={c.clean ? "ok" : "bad"}>{c.clean ? "clean" : `${c.findings.length} finding(s)`}</StatusPill>
        </div>
        {c.findings.length > 0 && (
          <ul className="mt-1 list-disc pl-5 text-[12px] text-muted">
            {c.findings.map((f) => <li key={f}>{f}</li>)}
          </ul>
        )}
      </div>
    ) : null;
  return (
    <div className="text-[12.5px]">
      {r.note && <p>{r.note}</p>}
      {one(r.check, "Check")}
      {one(r.before, "Before")}
      {r.repaired && r.repaired.length > 0 && <p className="mt-2">Repaired: {r.repaired.join(", ")}</p>}
      {r.failed && r.failed.length > 0 && <ErrorBox label="Not repaired" text={r.failed.join("\n")} />}
      {one(r.after, "After")}
    </div>
  );
}

// Checks (and repairs) a path's metadata with JuiceFS's own fsck. Repair touches only what the check itself
// reports repairable, then checks again.
function FsckPanel({ volumes }: { volumes: string[] }) {
  const { client } = useAuth();
  const [volume, setVolume] = React.useState(volumes[0] ?? "tenants");
  const [path, setPath] = React.useState("/");
  const body = (repair: boolean) => JSON.stringify({ volume, path, repair });
  // Repair is offered only for what a Check of THIS volume and path found repairable.
  const [last, setLast] = React.useState<{ key: string; repairable: number } | null>(null);
  const key = `${volume}:${path}`;
  const repairGate =
    !last || last.key !== key
      ? "Run Check on this volume and path first — Repair only fixes what the check reports."
      : last.repairable === 0
        ? "The last check found nothing repairable here."
        : undefined;
  return (
    <Section title="Metadata check & repair" hint="JuiceFS's own fsck on one path, recursively. Found stale directory link counts on 2026-09-26.">
      <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
        <select aria-label="Volume" className="rounded-md border border-neutral-300 px-2 py-1.5" value={volume} onChange={(e) => setVolume(e.target.value)}>
          {(volumes.length ? volumes : ["tenants", "platform"]).map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <input aria-label="Path within the volume" className="min-w-[18rem] flex-1 rounded-md border border-neutral-300 px-2 py-1.5 font-mono text-[12.5px]"
          value={path} onChange={(e) => setPath(e.target.value)} placeholder="/ozt100000/mail" />
      </div>
      <div className="space-y-3">
        <ActionCard
          title="Check"
          description="Read-only: lists what fsck finds under the path and which of it can be repaired."
          actionLabel="Check"
          danger="low"
          run={async () => {
            const r = await client.apiFetch<{ check?: FsckCheck }>("/juicefs/fsck", { method: "POST", body: body(false) });
            setLast({ key, repairable: r.check?.repairable.length ?? 0 });
            return r;
          }}
          renderResult={(r) => <FsckView value={r} />}
        />
        <ActionCard
          title="Repair"
          description="Repairs only the paths a fresh check reports repairable, then checks again and shows both."
          actionLabel="Repair"
          danger="medium"
          unavailable={repairGate}
          confirm={{
            title: `Repair ${volume}:${path}?`,
            body: <p className="text-[12.5px] leading-relaxed">This writes to the live volume's metadata. Only what fsck itself reports repairable is changed.</p>,
            confirmLabel: "Repair",
          }}
          run={async () => {
            const r = await client.apiFetch<{ after?: FsckCheck }>("/juicefs/fsck", { method: "POST", body: body(true) });
            setLast({ key, repairable: r.after?.repairable.length ?? 0 });
            return r;
          }}
          renderResult={(r) => <FsckView value={r} />}
        />
      </div>
    </Section>
  );
}
